import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  TrendingUp,
  MessageSquare,
  ShieldAlert,
  Send,
  AlertTriangle,
  Users,
  Sparkles,
  ArrowRight,
  Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import type { DashboardSummary, Comment, PublishJob } from "@/lib/types";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { EmptyState } from "@/components/shared/EmptyState";
import { IntentChip, PlatformChip, PublishChip } from "@/components/shared/Chips";
import { Button } from "@/components/ui/button";

const StatCard: React.FC<{
  label: string;
  value: number | string;
  hint?: string;
  icon: React.ReactNode;
  tone?: "default" | "danger" | "warning" | "success" | "violet";
  testId?: string;
}> = ({ label, value, hint, icon, tone = "default", testId }) => {
  const toneClass = {
    default: "text-[color:var(--sg-fg)]",
    danger: "text-[color:var(--sg-danger)]",
    warning: "text-[color:var(--sg-warning)]",
    success: "text-[color:var(--sg-success)]",
    violet: "text-[color:var(--sg-violet)]",
  }[tone];
  return (
    <div data-testid={testId} className="sg-card p-6 relative overflow-hidden">
      <div className="flex items-start justify-between">
        <div className="sg-label">{label}</div>
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center bg-[color:var(--sg-muted-bg)] ${toneClass}`}>
          {icon}
        </div>
      </div>
      <div className={`sg-stat-num mt-4 ${toneClass}`}>{value}</div>
      {hint && <div className="text-xs text-[color:var(--sg-muted)] mt-2">{hint}</div>}
    </div>
  );
};

export const Dashboard: React.FC = () => {
  const summary = useQuery<DashboardSummary>({
    queryKey: ["dashboard-summary"],
    queryFn: async () => (await api.get("/insights/dashboard")).data,
  });
  const recentComments = useQuery<Comment[]>({
    queryKey: ["recent-comments"],
    queryFn: async () => (await api.get("/comments?limit=6")).data,
  });
  const recentJobs = useQuery<PublishJob[]>({
    queryKey: ["recent-jobs"],
    queryFn: async () => (await api.get("/publish")).data,
  });

  const s = summary.data;

  return (
    <DashboardLayout
      title="Overview"
      subtitle="A live pulse of everything happening across your channels."
      seoPath="/dashboard"
      seoDescription="SociBaba dashboard — a live pulse of publishing, comments, leads, complaints and Discovery activity across your channels."
    >
      {summary.isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} className="sg-skeleton h-32" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Posts today"
            value={s?.posts_today ?? 0}
            icon={<Send className="h-4 w-4" strokeWidth={2} />}
            testId="stat-posts-today"
          />
          <StatCard
            label="Pending scheduled"
            value={s?.pending_scheduled ?? 0}
            icon={<Clock className="h-4 w-4" strokeWidth={2} />}
            tone="warning"
            testId="stat-pending"
          />
          <StatCard
            label="High-intent comments"
            value={s?.high_intent_comments ?? 0}
            icon={<TrendingUp className="h-4 w-4" strokeWidth={2} />}
            tone="violet"
            testId="stat-high-intent"
          />
          <StatCard
            label="Open complaints"
            value={s?.open_complaints ?? 0}
            icon={<ShieldAlert className="h-4 w-4" strokeWidth={2} />}
            tone="danger"
            testId="stat-open-complaints"
          />
          <StatCard
            label="Total products"
            value={s?.total_products ?? 0}
            icon={<Sparkles className="h-4 w-4" strokeWidth={2} />}
            testId="stat-products"
          />
          <StatCard
            label="Total comments"
            value={s?.total_comments ?? 0}
            icon={<MessageSquare className="h-4 w-4" strokeWidth={2} />}
            testId="stat-comments"
          />
          <StatCard
            label="Leads captured"
            value={s?.total_leads ?? 0}
            icon={<Users className="h-4 w-4" strokeWidth={2} />}
            tone="success"
            testId="stat-leads"
          />
          <StatCard
            label="Failed jobs"
            value={s?.failed_jobs ?? 0}
            icon={<AlertTriangle className="h-4 w-4" strokeWidth={2} />}
            tone="danger"
            testId="stat-failed"
          />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <div className="sg-card p-6 lg:col-span-2" data-testid="panel-recent-comments">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="sg-label">Recent comments</div>
              <div className="font-display text-lg font-semibold mt-1">
                Latest signals from your audience
              </div>
            </div>
            <Link to="/comments">
              <Button variant="ghost" size="sm" className="text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)]">
                View inbox <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </Link>
          </div>
          {recentComments.data && recentComments.data.length > 0 ? (
            <ul className="space-y-3">
              {recentComments.data.slice(0, 6).map((c) => (
                <li key={c.comment_id} className="flex items-start gap-3 py-2 border-b border-[color:var(--sg-border)] last:border-b-0">
                  <div className="h-8 w-8 rounded-full bg-[color:var(--sg-muted-bg)] shrink-0 flex items-center justify-center text-xs font-semibold text-[color:var(--sg-fg)]">
                    {c.author_handle.slice(1, 3).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-[color:var(--sg-fg)]">
                        {c.author_handle}
                      </span>
                      <PlatformChip platform={c.platform} />
                      <IntentChip intent={c.intent} />
                    </div>
                    <p className="text-sm text-[color:var(--sg-muted)] line-clamp-2">{c.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              title="No comments yet"
              description="Publish content or connect a channel to see comments flowing in."
            />
          )}
        </div>

        <div className="space-y-4">
          <div className="sg-card p-6" data-testid="panel-top-product">
            <div className="sg-label mb-2">Top performing</div>
            {s?.top_product ? (
              <>
                <div className="font-display text-lg font-semibold tracking-tight">
                  {s.top_product.title}
                </div>
                <div className="text-sm text-[color:var(--sg-muted)] mt-1">
                  {s.top_product.count} comments captured
                </div>
                <Link to={`/products/${s.top_product.product_id}`}>
                  <Button variant="link" size="sm" className="mt-2 px-0 text-[color:var(--sg-primary)]">
                    Inspect <ArrowRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </Link>
              </>
            ) : (
              <div className="text-sm text-[color:var(--sg-muted)]">
                Publish content to see winners.
              </div>
            )}
          </div>

          <div className="sg-card p-6" data-testid="panel-rising">
            <div className="sg-label mb-2 text-[color:var(--sg-violet)]">Rising trend</div>
            {s?.rising_product ? (
              <>
                <div className="font-display text-lg font-semibold tracking-tight">
                  {s.rising_product.title}
                </div>
                <div className="text-sm text-[color:var(--sg-muted)] mt-1">
                  {s.rising_product.count} comments in the past 7 days
                </div>
              </>
            ) : (
              <div className="text-sm text-[color:var(--sg-muted)]">Nothing hot yet.</div>
            )}
          </div>
        </div>
      </div>

      <div className="sg-card p-6 mt-4" data-testid="panel-recent-jobs">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="sg-label">Recent publish jobs</div>
            <div className="font-display text-lg font-semibold mt-1">Latest activity</div>
          </div>
          <Link to="/posts">
            <Button variant="ghost" size="sm" className="text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)]">
              All posts <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </Link>
        </div>
        {recentJobs.data && recentJobs.data.length > 0 ? (
          <div className="divide-y divide-[color:var(--sg-border)]">
            {recentJobs.data.slice(0, 5).map((j) => (
              <div key={j.job_id} className="py-3 flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{j.caption}</div>
                  <div className="text-xs text-[color:var(--sg-muted)] mt-0.5">
                    {new Date(j.created_at).toLocaleString()} · {j.targets.length} target(s)
                  </div>
                </div>
                <PublishChip status={j.overall_status} />
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            title="No publish jobs yet"
            description="Create a product and publish to a platform to see activity."
            action={
              <Link to="/upload">
                <Button className="bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white">
                  Upload content
                </Button>
              </Link>
            }
          />
        )}
      </div>
    </DashboardLayout>
  );
};
